import {t, Trans} from '@lingui/macro';
import _, {isEqual, isInteger, isUndefined} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {collectEnumValues} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {DropdownBox} from '../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useConstant} from '../../../srcCommon/helpers/hookHelpers';
import {
  LocalizeRenderer,
  useLocalize,
} from '../../../srcCommon/internationalization/internationalizationContext';
import {ColorDot} from '../../components/common/colorDot';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {inboxHasColor} from '../../helpers/modelHelpers';
import {renderAdvancedNotificationRuleSettingPart} from '../../helpers/models/notificationRulesHelpers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {InboxModel} from '../../models/api/inboxModel';
import {
  AdvancedNotificationRule,
  RTeammateAdvancedNotificationNamesEnum,
  RTeammateBasicNotificationTypesEnum,
  TeammateAdvancedNotificationNamesEnum,
  TeammateBasicNotificationTypesEnum,
} from '../../models/api/notificationRulesModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsNotificationsUrl,
  SettingsResourcePagesEnum,
  SettingsTabsPagesEnum,
} from '../../models/url/settingsUrlModel';
import {getUserParticipatingInboxes} from '../../store/data/inboxes/inboxesSelectors';
import {
  deleteAdvancedNotificationRule,
  updateAdvancedNotificationRule,
} from '../../store/data/teammates/teammatesActions';
import {getUserNotificationRules} from '../../store/data/teammates/teammatesSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {
  SettingsFooter,
  StyledSettingsFormContent,
  StyledSettingsFormHeaderButtons,
  StyledSettingsFormMain,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {isExpressionRef} from '../editors/expressions/expressionRefModel';
import {MultipleChoiceDropdown} from '../rules/ruleEditor/dropdowns/multipleChoiceDropdown';
import {SingleChoiceDropdown} from '../rules/ruleEditor/dropdowns/singleChoiceDropdown';
import {defaultDropdownWidth} from '../rules/ruleEditor/parameters/genericParameter';
import {RuleMultilineParameterCard} from '../rules/ruleEditor/ruleMultilineParameterCard';
import {SettingsHeader} from '../settingsHeader';

/*
 * Props.
 */

interface SettingsSingleAdvancedNotificationProps {
  url: SettingsNotificationsUrl;
}

/*
 * Style.
 */

const StyledTitleDiv = styled.div`
  display: flex;
  gap: 8px;
`;

const RuleEditorSectionWrap = styled.div`
  margin-bottom: 25px;
  display: grid;
  grid-row-gap: 10px;
`;

const RuleEditorSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const RuleEditorSectionDescription = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
`;

const StyledActionValueSpan = styled.div`
  &::first-letter {
    text-transform: uppercase;
  }
`;

/*
 * Component.
 */

const itemsLabelRenderer: {[k in TeammateAdvancedNotificationNamesEnum]: LocalizeRenderer} = {
  [TeammateAdvancedNotificationNamesEnum.INBOUND_ANY_MESSAGE]: (localize) =>
    localize(t('advanced-notification-inbound-any-message')`A message arrives in`),
  [TeammateAdvancedNotificationNamesEnum.OUTBOUND_ANY_MESSAGE]: (localize) =>
    localize(t('advanced-notification-outbound-any-message')`A new message is sent from`),
  [TeammateAdvancedNotificationNamesEnum.CONVERSATION_ASSIGNED]: (localize) =>
    localize(t('advanced-notification-conversation-assigned')`I am assigned a new conversation from`),
  [TeammateAdvancedNotificationNamesEnum.MENTION]: (localize) =>
    localize(t('advanced-notification-mention')`I am @mentioned in a comment in`),
  [TeammateAdvancedNotificationNamesEnum.TRACKER_SHARE]: (localize) =>
    localize(t('advanced-notification-tracker-share')`I am invited to a new conversation in`),
  [TeammateAdvancedNotificationNamesEnum.REMINDER]: (localize) =>
    localize(t('advanced-notification-reminder')`I receive a reminder for a snoozed conversation in`),
  [TeammateAdvancedNotificationNamesEnum.POST_IN_ASSIGNED]: (localize) =>
    localize(
      t('advanced-notification-post-in-assigned')`A new activity occurs in my assigned conversation from`,
    ),
  [TeammateAdvancedNotificationNamesEnum.POST_IN_FOLLOWED]: (localize) =>
    localize(t('advanced-notification-post-in-followed')`A new activity occurs in conversation I follow in`),
  [TeammateAdvancedNotificationNamesEnum.RECIPIENT_ERROR]: (localize) =>
    localize(t('advanced-notification-recipient-error')`A sent message failed to be delivered from`),
  [TeammateAdvancedNotificationNamesEnum.RULE_NOTIFY]: (localize) =>
    localize(t('advanced-notification-rule-notify')`I am notified by a rule automatically`),
};

const items = collectEnumValues(TeammateAdvancedNotificationNamesEnum).map((item) => ({
  id: item,
  label: itemsLabelRenderer[item],
}));

export const SettingsSingleAdvancedNotification: FC<SettingsSingleAdvancedNotificationProps> = (props) => {
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();
  const {showBanner} = useSettingsBanners();
  const localize = useLocalize();
  const inboxes = useAppSelector(getUserParticipatingInboxes);
  const advancedNotificationRules = useAppSelector(getUserNotificationRules).advanced;
  const isEditing = isInteger(props.url.notificationIndex);
  const editedAdvancedNotificationRule = useConstant(() =>
    !isUndefined(props.url.notificationIndex)
      ? advancedNotificationRules[props.url.notificationIndex]
      : undefined,
  );
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const [selectedInboxIds, setSelectedInboxIds] = useState<ReadonlyArray<number>>(
    editedAdvancedNotificationRule?.inboxIds || [],
  );
  const [name, setName] = useState<TeammateAdvancedNotificationNamesEnum>(
    editedAdvancedNotificationRule?.name || TeammateAdvancedNotificationNamesEnum.CONVERSATION_ASSIGNED,
  );
  const [isDirty, setIsDirty] = useState(false);
  const initialDestinations = useMemo(
    () => ({
      [TeammateBasicNotificationTypesEnum.APP]: false,
      [TeammateBasicNotificationTypesEnum.MOBILE]: false,
      [TeammateBasicNotificationTypesEnum.EMAIL]: false,
    }),
    [],
  );

  const [destinations, setDestinations] = useState<AdvancedNotificationRule['setting']>(
    editedAdvancedNotificationRule?.setting || initialDestinations,
  );
  const destinationItems = useMemo(
    () => [
      {
        id: TeammateBasicNotificationTypesEnum.APP,
        label: <Trans id="settingsnotifications-notifybydesktop">Notify me on desktop</Trans>,
      },
      {
        id: TeammateBasicNotificationTypesEnum.MOBILE,
        label: <Trans id="settingsnotifications-notifybymobile">Notify me on mobile</Trans>,
      },
      {
        id: TeammateBasicNotificationTypesEnum.EMAIL,
        label: <Trans id="settingsnotifications-notifybyemail">Notify me by email</Trans>,
      },
    ],
    [],
  );

  const onCancelClick = () => {
    navigateToLink(
      buildSettingsPath({
        ...props.url,
        tabPage: SettingsTabsPagesEnum.ADVANCED,
        notificationIndex: undefined,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      }),
    );
  };

  const onDeleteClick = async () => {
    if (!isEditing || isUndefined(props.url.notificationIndex)) {
      return;
    }

    await dispatch(deleteAdvancedNotificationRule(props.url.notificationIndex));
    navigateToLink(
      buildSettingsPath({
        ...props.url,
        tabPage: SettingsTabsPagesEnum.ADVANCED,
        notificationIndex: undefined,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      }),
    );
  };
  const onSaveClick = async () => {
    await dispatch(
      updateAdvancedNotificationRule(
        {
          ...(editedAdvancedNotificationRule || {}),
          name,
          setting: destinations,
          isInAllInboxes: selectedInboxIds.length === 0,
          inboxIds: selectedInboxIds,
        },
        props.url.notificationIndex,
      ),
    );
    showBanner({
      message: isEditing ? (
        <Trans id="settingsnotifications-notificationupdated">Notification rule has been updated.</Trans>
      ) : (
        <Trans id="settingsnotifications-notificationcreated">Notification rule has been created.</Trans>
      ),
      type: SettingsBannersEnum.SUCCESS,
    });
    navigateToLink(
      buildSettingsPath({
        ...props.url,
        tabPage: SettingsTabsPagesEnum.ADVANCED,
        notificationIndex: undefined,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      }),
    );
  };
  const canSave = useMemo(
    () =>
      isDirty &&
      (name !== editedAdvancedNotificationRule?.name ||
        !isEqual(destinations, editedAdvancedNotificationRule?.setting) ||
        !isEqual(selectedInboxIds, editedAdvancedNotificationRule?.inboxIds || [])),
    [
      destinations,
      editedAdvancedNotificationRule?.inboxIds,
      editedAdvancedNotificationRule?.name,
      editedAdvancedNotificationRule?.setting,
      isDirty,
      name,
      selectedInboxIds,
    ],
  );
  const theme = useUserTheme();

  const maybeRenderFooter = () => {
    if (!isDirty && isEditing) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSaveClick} isDisabled={!canSave}>
          {isEditing ? (
            <Trans id="settingstags-savenotificationrule">Save</Trans>
          ) : (
            <Trans id="settingstags-createnotificationrule">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  const renderConditionTitle = () => (
    <StyledTitleDiv>
      <SingleChoiceDropdown
        items={items}
        onChange={(newName) => {
          setIsDirty(true);
          if (!RTeammateAdvancedNotificationNamesEnum.is(newName)) {
            return;
          }
          setName(newName);
        }}
        getItemKey={(i) => i.id}
        renderItemLabel={(i) => i.label(localize)}
        selectedItem={name}
        isSearchDisabled
        width={450}
        serializeItemForSearch={(i) => i.label(localize)}
      />
      {name !== TeammateAdvancedNotificationNamesEnum.RULE_NOTIFY && (
        <MultipleChoiceDropdown<InboxModel>
          onChange={(newSelectedInboxes) => {
            setIsDirty(true);
            const newInboxIds = newSelectedInboxes?.filter((item): item is number => !isExpressionRef(item));

            if (!newInboxIds) {
              return;
            }

            setSelectedInboxIds(newInboxIds);
          }}
          selectedItems={selectedInboxIds}
          items={inboxes}
          getItemKey={(item) => String(item.id)}
          renderItemLabel={(item) => item.name}
          renderItemIcon={(item) => (
            <ColorDot size={8} color={inboxHasColor(item) ? item.color : theme.greys.shade40} />
          )}
          serializeItemForSearch={(item) => item.name}
          placeholder={<Trans id="rule-editor-select-inboxes">Select inboxes</Trans>}
          anyLabel={<Trans id="rule-editor-any-inbox">Any inbox</Trans>}
          anyIcon={<Icon name="inbox" color={theme.greys.shade60} />}
          canSelectAnyItem
          sortSelectedItemsToTop
          width={defaultDropdownWidth}
          title={<Trans id="rule-editor-inboxes-group">Inboxes</Trans>}
        />
      )}
    </StyledTitleDiv>
  );

  const selectedDestinations = useMemo(
    () =>
      _(destinations)
        .entries()
        .filter((pair) => pair[1])
        .map((pair) => RTeammateBasicNotificationTypesEnum.is(pair[0]) && pair[0])
        .compact()
        .value(),
    [destinations],
  );

  const renderActionTitle = useCallback(
    () => (
      <StyledTitleDiv>
        <MultipleChoiceDropdown<typeof destinationItems[number]>
          onChange={(newDestinations) => {
            setIsDirty(true);
            if (
              !newDestinations ||
              newDestinations.length < 1 ||
              newDestinations.some((i) => isExpressionRef(i))
            ) {
              setDestinations(initialDestinations);
            } else {
              const final = {...initialDestinations};
              newDestinations.filter(RTeammateBasicNotificationTypesEnum.is).forEach((v) => {
                final[v] = true;
              });
              setDestinations(final);
            }
          }}
          selectedItems={selectedDestinations}
          items={destinationItems}
          getItemKey={(item) => String(item.id)}
          renderItemLabel={(item) => item.label}
          serializeItemForSearch={(item) => String(item.label)}
          isSearchDisabled
          width={defaultDropdownWidth}
          anyLabel={<Trans id="settingsnotifications-donotnotify">Don&apos;t notify me</Trans>}
          canSelectAnyItem
          renderButton={(isOpen, popoverProps) => (
            <DropdownBox
              {...popoverProps}
              hasHover={isOpen}
              noInnerMargin
              value={
                <StyledActionValueSpan>
                  {renderAdvancedNotificationRuleSettingPart(destinations)}
                </StyledActionValueSpan>
              }
            />
          )}
        />
      </StyledTitleDiv>
    ),
    [destinationItems, destinations, initialDestinations, selectedDestinations],
  );

  const maybeRenderDeleteConfirmDialog = () => {
    if (!isDeleteConfirmationOpen) {
      return null;
    }

    return (
      <OkCancelDialog
        title={
          <Trans id="settingsnotifications-deletenotification-title">Delete this notification rule?</Trans>
        }
        onOkClick={onDeleteClick}
        okButtonVariant={ButtonVariantsEnum.DANGER}
        okButtonLabel={
          <Trans id="settingsnotifications-deletenotification-ok">Delete notification rule</Trans>
        }
        onRequestClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <Trans id="settingsnotifications-deletenotification-copy">This action cannot be undone</Trans>
      </OkCancelDialog>
    );
  };

  return (
    <StyledSettingsWrapper>
      <SettingsHeader
        topLine={
          <SettingsBreadcrumbs
            showSeparatorAfterTopLevel
            breadcrumbs={[
              {
                label: <Trans id="settingsnotifications-breadcrumb-advanced">Advanced</Trans>,
                onBreadcrumbClick: () => {
                  navigateToLink(
                    buildSettingsPath({
                      ...props.url,
                      tabPage: SettingsTabsPagesEnum.ADVANCED,
                      notificationIndex: undefined,
                      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
                    }),
                  );
                },
              },
            ]}
            topLevelBreadcrumb={{
              label: <Trans id="settingsnotifications-breadcrumb-notifications">Notifications</Trans>,
              onBreadcrumbClick: () => {
                navigateToLink(
                  buildSettingsPath({
                    ...props.url,
                    tabPage: SettingsTabsPagesEnum.BASIC,
                    notificationIndex: undefined,
                    resourcePage: SettingsResourcePagesEnum.OVERVIEW,
                  }),
                );
              },
            }}
          />
        }
        right={
          isEditing ? (
            <StyledSettingsFormHeaderButtons>
              <Button
                size={VisualSizesEnum.MEDIUM}
                variant={ButtonVariantsEnum.SECONDARY_DANGER}
                color={PaletteColorsEnum.RED}
                onClick={() => setIsDeleteConfirmationOpen(true)}
              >
                <Trans id="settings-deletenotification">Delete notification rule</Trans>
              </Button>
            </StyledSettingsFormHeaderButtons>
          ) : null
        }
      >
        <Trans id="settingsnotifications-addnotificationrule">Add notification rule</Trans>
      </SettingsHeader>

      <StyledSettingsFormMain>
        <StyledSettingsFormContent>
          <RuleEditorSectionWrap>
            <RuleEditorSectionHeader>
              <RuleEditorSectionDescription>
                <Trans id="settingsnotifications-editor-if">If...</Trans>
              </RuleEditorSectionDescription>
            </RuleEditorSectionHeader>
            <div>
              <RuleMultilineParameterCard title={renderConditionTitle()} icon={<Icon name="inboxFilled" />} />
            </div>
          </RuleEditorSectionWrap>
          <RuleEditorSectionWrap>
            <RuleEditorSectionHeader>
              <RuleEditorSectionDescription>
                <Trans id="settingsnotifications-editor-then">Then...</Trans>
              </RuleEditorSectionDescription>
            </RuleEditorSectionHeader>
            <div>
              <RuleMultilineParameterCard title={renderActionTitle()} icon={<Icon name="bell" />} />
            </div>
          </RuleEditorSectionWrap>
        </StyledSettingsFormContent>
      </StyledSettingsFormMain>
      {maybeRenderFooter()}
      {maybeRenderDeleteConfirmDialog()}
    </StyledSettingsWrapper>
  );
};
